import { getLanguage } from '@lemonade-hq/lemonation';
import axios from 'axios';
import { getLocaleFromHtml } from '../html.esm.js';
import { getEnv, Env } from '../infra.esm.js';
import { axiosCommonErrorInterceptor, setupAxiosCommonErrorInterceptor } from './client_errors.esm.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _ts_generator(thisArg, body) {
    var f, y, t, g, _ = {
        label: 0,
        sent: function() {
            if (t[0] & 1) throw t[1];
            return t[1];
        },
        trys: [],
        ops: []
    };
    return g = {
        next: verb(0),
        "throw": verb(1),
        "return": verb(2)
    }, typeof Symbol === "function" && (g[Symbol.iterator] = function() {
        return this;
    }), g;
    function verb(n) {
        return function(v) {
            return step([
                n,
                v
            ]);
        };
    }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while(_)try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [
                op[0] & 2,
                t.value
            ];
            switch(op[0]){
                case 0:
                case 1:
                    t = op;
                    break;
                case 4:
                    _.label++;
                    return {
                        value: op[1],
                        done: false
                    };
                case 5:
                    _.label++;
                    y = op[1];
                    op = [
                        0
                    ];
                    continue;
                case 7:
                    op = _.ops.pop();
                    _.trys.pop();
                    continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) {
                        _ = 0;
                        continue;
                    }
                    if (op[0] === 3 && (!t || op[1] > t[0] && op[1] < t[3])) {
                        _.label = op[1];
                        break;
                    }
                    if (op[0] === 6 && _.label < t[1]) {
                        _.label = t[1];
                        t = op;
                        break;
                    }
                    if (t && _.label < t[2]) {
                        _.label = t[2];
                        _.ops.push(op);
                        break;
                    }
                    if (t[2]) _.ops.pop();
                    _.trys.pop();
                    continue;
            }
            op = body.call(thisArg, _);
        } catch (e) {
            op = [
                6,
                e
            ];
            y = 0;
        } finally{
            f = t = 0;
        }
        if (op[0] & 5) throw op[1];
        return {
            value: op[0] ? op[1] : void 0,
            done: true
        };
    }
}
var DEFAULT_TIMEOUT = 30000;
var DEV_TIMEOUT = 999999999;
function createAxiosInstance() {
    var locale = getLocaleFromHtml();
    var instance = axios.create();
    var timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    instance.defaults.withCredentials = true;
    instance.defaults.headers.common["Content-Type"] = "application/json";
    instance.defaults.headers.common.Accept = "application/json";
    if (timeZone != null) instance.defaults.headers.common.timezone = timeZone; // eslint-disable-line @typescript-eslint/no-unnecessary-condition
    // @ts-expect-error Cypress is defined only when running E2E tests
    instance.defaults.timeout = getEnv() === Env.Development || window.Cypress != null ? DEV_TIMEOUT : DEFAULT_TIMEOUT;
    instance.defaults.headers.common["X-Come-Join-Us"] = "makers.lemonade.com";
    if (locale != null) {
        instance.defaults.headers.common["Accept-Language"] = getLanguage(locale);
    }
    setupAxiosCommonErrorInterceptor(instance);
    return instance;
}
var _instance;
// for tests
function resetAxiosInstance() {
    _instance = undefined;
}
function getAxiosInstance() {
    _instance = _instance != null ? _instance : createAxiosInstance();
    return _instance;
}
function request(config) {
    return _request.apply(this, arguments);
}
function _request() {
    _request = _async_to_generator(function(config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().request(config)
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _request.apply(this, arguments);
}
function get(url, config) {
    return _get.apply(this, arguments);
}
function _get() {
    _get = _async_to_generator(function(url, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().get(url, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _get.apply(this, arguments);
}
function head(url, config) {
    return _head.apply(this, arguments);
}
function _head() {
    _head = _async_to_generator(function(url, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().head(url, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _head.apply(this, arguments);
}
function post(url, body, config) {
    return _post.apply(this, arguments);
}
function _post() {
    _post = _async_to_generator(function(url, body, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().post(url, body, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _post.apply(this, arguments);
}
function put(url, body, config) {
    return _put.apply(this, arguments);
}
function _put() {
    _put = _async_to_generator(function(url, body, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().put(url, body, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _put.apply(this, arguments);
}
function patch(url, body, config) {
    return _patch.apply(this, arguments);
}
function _patch() {
    _patch = _async_to_generator(function(url, body, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().patch(url, body, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _patch.apply(this, arguments);
}
function del(url, config) {
    return _del.apply(this, arguments);
}
function _del() {
    _del = _async_to_generator(function(url, config) {
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    return [
                        4,
                        getAxiosInstance().delete(url, config).then(function(response) {
                            return response.data;
                        })
                    ];
                case 1:
                    return [
                        2,
                        _state.sent()
                    ];
            }
        });
    });
    return _del.apply(this, arguments);
}
var Client = {
    axiosCommonErrorInterceptor: axiosCommonErrorInterceptor,
    setupAxiosCommonErrorInterceptor: setupAxiosCommonErrorInterceptor,
    getAxiosInstance: getAxiosInstance,
    request: request,
    get: get,
    head: head,
    post: post,
    put: put,
    patch: patch,
    del: del
};

export { Client, getAxiosInstance, resetAxiosInstance };
